import { EventEmitter, Injectable, Input, Output, computed, signal, inject, DestroyRef } from '@angular/core';
import { OnDestroyMixin } from '@w11k/ngx-componentdestroyed';
import { FieldDef } from 'app/core/models';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReverseGeoCode } from '@modules/research/research.types';

export interface FormFieldInterfaceComponent {
  model: any;
  fieldDef: any;
  update: any;
  data: any;
  readonly?: boolean;
  isRequired?: boolean;
  fieldMask?: (fieldValue?: string) => string;
  updateChangedModel?(model: any): any;
  modelUpdated?: Subject<void>;
}

export interface FieldStyling {
  color: string; // hex color
  is_bold: boolean;
}

@Injectable()
export abstract class FormFieldComponent extends OnDestroyMixin implements FormFieldInterfaceComponent {
  @Input() model: any;
  @Input() fieldDef: FieldDef;
  @Input() data: any;
  @Input() readonly: boolean = false;
  @Input() fieldMask?: (fieldValue: string) => string;
  @Input() isRequired: boolean = false;
  @Output() update: EventEmitter<PartialFormField> = new EventEmitter();

  showTip: string;
  enableStyling = signal(true);
  fieldStyling = computed(() => {
    if (this.enableStyling() && !this.isDisabled) {
      const color = this.model?.meta?.[this.fieldDef?.name]?.conditional_styling?.color;
      const is_bold = !!this.model?.meta?.[this.fieldDef?.name]?.conditional_styling?.is_bold;

      return { color, is_bold };
    }
    return FormFieldComponent.emptyStyling;
  });
  readonly dRef = inject(DestroyRef);

  readonly mask$ = new BehaviorSubject<string>('');
  readonly modelUpdated = new Subject<void>();
  readonly commonTargetStylingClass: string = '.mat-form-field-wrapper .mat-form-field-flex';

  protected static emptyStyling: FieldStyling = { color: null, is_bold: false };

  protected get isReadonly(): boolean {
    return this.readonly || this.fieldDef?.meta?.readonly;
  }

  get isModelArchived(): boolean {
    return !!this.model?.archived_at;
  }

  get isDisabled(): boolean {
    return this.isModelArchived || this.isReadonly || this.fieldDef?.locked || this.fieldDef?.meta?.value_hidden;
  }

  abstract updateChangedModel?(model: any): any;

  ngOnInit(): void {
    this.showTip = this.fieldDef.description || this.fieldDef.meta?.calc_code;

    this.modelUpdated.pipe(takeUntilDestroyed(this.dRef)).subscribe(() => {
      this.enableStyling.set(true);
    });

    this.update.pipe(takeUntilDestroyed(this.dRef)).subscribe(() => {
      this.enableStyling.set(false);
    });
  }
}

export interface PartialFormField {
  [key: string]: string | number | string[] | ReverseGeoCode;
  id?: number;
}
